import { Link } from 'react-router';
import { ArticleLayout, H2, P, UL, LI, Callout } from './ArticleLayout';

const rows: { dimension: string; traditional: string; ai: string }[] = [
  {
    dimension: 'Channel watched',
    traditional: 'Email, USB, file uploads, network egress',
    ai: 'AI prompts, pastes and file uploads to AI services',
  },
  {
    dimension: 'Visibility of AI use',
    traditional: 'Blind — prompts look like ordinary HTTPS to trusted domains',
    ai: 'Purpose-built to see and classify AI interactions',
  },
  {
    dimension: 'Deployment',
    traditional: 'Network gateways, email proxies, agents',
    ai: 'Endpoint inspection at the OS level — browser, desktop apps and IDEs',
  },
  {
    dimension: 'Coverage of personal accounts',
    traditional: 'None if it bypasses SSO / corporate network',
    ai: 'Covered — detection is on the device, not the account',
  },
  {
    dimension: 'Where content is inspected',
    traditional: 'Often uploaded to a cloud service to be scanned',
    ai: 'Locally on the endpoint — content never leaves the device',
  },
];

export function AiDlpVsTraditionalDlp() {
  return (
    <ArticleLayout
      currentPath="/ai-dlp-vs-traditional-dlp"
      breadcrumb="AI DLP vs traditional DLP"
      eyebrow="Comparison"
      title={
        <>
          AI DLP vs traditional DLP: what&apos;s the <span className="text-red-accent">difference?</span>
        </>
      }
      lead="Traditional data loss prevention was designed for email, USB drives and file transfers. Generative AI created a new exfiltration channel — the prompt — that legacy DLP was never built to see. Here is where the two differ and why it matters."
    >
      <H2 id="what-traditional-dlp-does">What traditional DLP does well</H2>
      <P>
        Traditional DLP has protected organisations for years by watching well-understood channels:
        outbound email, removable media, printing, and file transfers. It inspects those channels
        against policy and blocks or quarantines violations. For those use cases it remains essential.
      </P>

      <H2 id="where-it-falls-short">Where it falls short on AI</H2>
      <P>
        The gap is not that legacy DLP is bad — it is that AI prompts do not look like the channels it
        watches. A prompt to ChatGPT is an ordinary HTTPS request to a trusted domain. It carries no
        attachment header, no email recipient, no file-transfer signature. So:
      </P>
      <UL>
        <LI>Network and email DLP never see the prompt content at all.</LI>
        <LI>Personal AI accounts bypass corporate SSO and monitoring entirely.</LI>
        <LI>Desktop apps and IDE assistants sidestep browser-only controls.</LI>
        <LI>Blocking whole domains just moves usage to unmonitored devices.</LI>
      </UL>

      <H2 id="comparison">Side by side</H2>
      <div className="my-6 overflow-x-auto">
        <table className="w-full border-collapse text-left text-sm">
          <thead>
            <tr className="border-b border-border">
              <th className="py-3 pr-4 font-semibold text-text-primary"> </th>
              <th className="py-3 pr-4 font-semibold text-text-secondary">Traditional DLP</th>
              <th className="py-3 font-semibold text-red-accent">AI DLP</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.dimension} className="border-b border-border/60 align-top">
                <td className="py-3 pr-4 font-medium text-text-primary">{r.dimension}</td>
                <td className="py-3 pr-4 text-text-secondary">{r.traditional}</td>
                <td className="py-3 text-text-secondary">{r.ai}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <H2 id="do-you-need-both">Do you need both?</H2>
      <P>
        Usually, yes. AI DLP does not replace traditional DLP — it covers a channel traditional DLP was
        never designed for. Most organisations keep their email and network DLP for classic
        exfiltration and add AI-native, endpoint DLP to close the generative-AI gap. Together they cover
        the full picture.
      </P>

      <Callout title="AI-native and endpoint-based">
        <P>
          Redbax is AI DLP built for the prompt: it inspects content at the OS level on Windows and
          Linux, classifies PII, PCI, credentials and source code locally, and applies block / warn /
          log policy before anything reaches an AI service. Start with the{' '}
          <Link to="/ai-dlp" className="text-red-accent underline underline-offset-4 hover:text-red-hover">
            AI DLP guide
          </Link>{' '}
          or see the{' '}
          <Link to="/features" className="text-red-accent underline underline-offset-4 hover:text-red-hover">
            features
          </Link>
          .
        </P>
      </Callout>
    </ArticleLayout>
  );
}
